import type { GameCard } from "./deck.js";

export interface SymbolPlacement {
  symbolId: number;
  x: number;
  y: number;
  size: number;
  rotation: number;
}

// Every square fits inside its circumcircle at every rotation. Leave space
// between those circles and stay strictly inside the 0.49 cutting limit.
const CARD_RADIUS = 0.485;
const SYMBOL_GAP = 0.012;
const PACKING_STARTS = 64;
const CANDIDATES_PER_SYMBOL = 144;
const TAU = Math.PI * 2;
const layoutCache = new Map<string, readonly SymbolPlacement[]>();

// Sample the entire printable disk, including the lower edge. A complete
// packing is scored for empty areas instead of accepting the first legal one.
const COVERAGE_POINTS = [
  [0.5, 0.5],
  ...[0.16, 0.3, 0.415].flatMap((radius, ring) =>
    Array.from({ length: 16 }, (_, i) => {
      const angle = ((i + ring / 3) * TAU) / 16;
      return [0.5 + Math.cos(angle) * radius, 0.5 + Math.sin(angle) * radius];
    }),
  ),
];

type SymbolCount = 4 | 6 | 8;
type Piece = Pick<SymbolPlacement, "symbolId" | "size" | "rotation">;
type FallbackPoint = readonly [x: number, y: number, size: number];

const SIZE_RANGES: Record<SymbolCount, readonly [number, number]> = {
  4: [0.2, 0.27],
  6: [0.16, 0.2],
  8: [0.13, 0.19],
};

// Each card has one clearly larger image, including fallback layouts. The
// shuffled assignment keeps a repeated symbol from always being the large one.
const LARGE_SIZE_RANGES: Record<SymbolCount, readonly [number, number]> = {
  4: [0.34, 0.36],
  6: [0.28, 0.3],
  8: [0.28, 0.3],
};

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function layoutSeed(cardId: number, symbols: readonly number[]): number {
  let seed = 0x811c9dc5;
  for (const value of [cardId, symbols.length, ...symbols]) {
    seed = Math.imul(seed ^ value, 0x01000193);
    seed ^= seed >>> 16;
  }
  return seed >>> 0;
}

function fits(
  x: number,
  y: number,
  radius: number,
  placed: readonly SymbolPlacement[],
): boolean {
  return placed.every((other) => {
    const distance = radius + other.size / Math.SQRT2 + SYMBOL_GAP;
    return (x - other.x) ** 2 + (y - other.y) ** 2 >= distance ** 2;
  });
}

function coverageScore(placed: readonly SymbolPlacement[]): number {
  let worstGap = 0;
  let squaredGaps = 0;
  let area = 0;
  let massX = 0;
  let massY = 0;
  for (const p of placed) {
    const weight = p.size ** 2;
    area += weight;
    massX += (p.x - 0.5) * weight;
    massY += (p.y - 0.5) * weight;
  }
  for (const [x, y] of COVERAGE_POINTS) {
    const gap = Math.max(0, Math.min(...placed.map((p) =>
      Math.hypot(x - p.x, y - p.y) - p.size * 0.42,
    )));
    worstGap = Math.max(worstGap, gap);
    squaredGaps += gap ** 2;
  }
  return worstGap * 1.5 + Math.sqrt(squaredGaps / COVERAGE_POINTS.length) * 0.6 +
    Math.hypot(massX / area, massY / area) * 0.7;
}

/** A finite search over legal, spatially balanced packings. */
function pack(
  pieces: readonly Piece[],
  random: () => number,
): SymbolPlacement[] | null {
  // Coverage alone rewards a large image in the middle surrounded by a crown.
  // Give dense cards a seeded radial family instead: most large images live
  // on a flank, leaving room for smaller images at several interior depths.
  const largeBand = pieces.length === 8
    ? random() < 0.82 ? [0.185, 0.25] : [0.04, 0.145]
    : null;
  const largeAngle = random() * TAU;
  let best: SymbolPlacement[] | null = null;
  let bestScore = Infinity;
  for (let start = 0; start < PACKING_STARTS; start += 1) {
    const placed: SymbolPlacement[] = [];
    for (const [index, piece] of pieces.entries()) {
      const radius = piece.size / Math.SQRT2;
      const availableRadius = CARD_RADIUS - radius;
      let accepted = false;
      for (let candidate = 0; candidate < CANDIDATES_PER_SYMBOL; candidate += 1) {
        // Uniform disk samples: no center slot, fixed rows or ring positions.
        const anchored = index === 0 && largeBand;
        const angle = anchored ? largeAngle + (random() - 0.5) * 2 : random() * TAU;
        const distance = anchored
          ? largeBand[0] + random() * (Math.min(largeBand[1], availableRadius) - largeBand[0])
          : Math.sqrt(random()) * availableRadius;
        const x = 0.5 + Math.cos(angle) * distance;
        const y = 0.5 + Math.sin(angle) * distance;
        if (!fits(x, y, radius, placed)) continue;
        placed.push({ ...piece, x, y });
        accepted = true;
        break;
      }
      if (!accepted) break;
    }
    if (placed.length === pieces.length) {
      const score = coverageScore(placed);
      if (score < bestScore) {
        best = placed;
        bestScore = score;
      }
    }
  }
  if (!best) return null;
  let refined: SymbolPlacement[] = best;
  // Small legal moves refine the empty areas without snapping to a ring/grid.
  for (let step = 0; step < 128; step += 1) {
    const index = Math.floor(random() * refined.length);
    const piece = refined[index];
    const x = piece.x + (random() - 0.5) * 0.065;
    const y = piece.y + (random() - 0.5) * 0.065;
    const radius = piece.size / Math.SQRT2;
    const distance = Math.hypot(x - 0.5, y - 0.5);
    if (distance + radius > CARD_RADIUS) continue;
    if (index === 0 && largeBand && (distance < largeBand[0] || distance > largeBand[1])) continue;
    if (!fits(x, y, radius, refined.filter((_, i) => i !== index))) continue;
    const candidate: SymbolPlacement[] = refined.map((p, i) => i === index ? { ...p, x, y } : p);
    const score = coverageScore(candidate);
    if (score < bestScore) {
      refined = candidate;
      bestScore = score;
    }
  }
  return refined;
}

/**
 * Conservative irregular packings used only if all search starts fail. Each
 * template has >0.012 separation between circumcircles and an outer extent
 * <0.485. A rigid rotation preserves both guarantees and varies it by card.
 */
function fallback(
  pieces: readonly Piece[],
  count: SymbolCount,
  random: () => number,
): SymbolPlacement[] {
  const points: FallbackPoint[] =
    count === 4
      ? [
          [-0.19, -0.12, 0.34],
          [0.22, -0.16, 0.22],
          [-0.08, 0.27, 0.21],
          [0.25, 0.17, 0.2],
        ]
      : count === 6
        ? [
            [0.1721, 0.1887, 0.28],
            [-0.2265, -0.2562, 0.19],
            [-0.3234, 0.0243, 0.175],
            [0.0686, -0.3216, 0.165],
            [0.2603, -0.1672, 0.16],
            [-0.1643, 0.2528, 0.16],
          ]
        : [
            [-0.2189, 0.035, 0.28],
            [0.2983, -0.1659, 0.18],
            [0.1522, 0.2681, 0.17],
            [-0.2192, -0.2927, 0.15],
            [0.0385, -0.2784, 0.14],
            [-0.1113, 0.3577, 0.135],
            [0.37, 0.0673, 0.13],
            [0.1244, 0.0006, 0.13],
          ];
  const angle = random() * TAU;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  const placed = points.map(([x, y, size], i) => ({
    ...pieces[i],
    size,
    x: 0.5 + x * cosine - y * sine,
    y: 0.5 + x * sine + y * cosine,
  }));
  // A legal local perturbation avoids repeating a rigid template on the rare
  // cards where every search start fails. Never trade safety for variation.
  for (let index = 0; index < placed.length; index++) {
    const p = placed[index];
    const x = p.x + (random() - 0.5) * 0.006;
    const y = p.y + (random() - 0.5) * 0.006;
    const radius = p.size / Math.SQRT2;
    if (Math.hypot(x - 0.5, y - 0.5) + radius <= CARD_RADIUS &&
      fits(x, y, radius, placed.filter((_, i) => i !== index))) {
      placed[index] = { ...p, x, y };
    }
  }
  return placed;
}

/**
 * Normalized geometry shared by screen and PDF, seeded by card ID and its
 * canonical symbol set. Large symbols are placed first, at unrelated positions,
 * with different sizes and a full 360° rotation range. No DOM or global RNG.
 */
export function layoutSymbols(card: GameCard): SymbolPlacement[] {
  const count = card.symbols.length;
  if (count !== 4 && count !== 6 && count !== 8)
    throw new RangeError("Une carte doit contenir 4, 6 ou 8 symboles.");

  // Canonicalize a copy: shuffled deck order cannot alter screen/PDF geometry.
  const symbols = [...card.symbols].sort((a, b) => a - b);
  const key = `${card.id}:${symbols.join(",")}`;
  const cached = layoutCache.get(key);
  if (cached) return cached.map((p) => ({ ...p }));
  const random = seededRandom(layoutSeed(card.id, symbols));
  for (let i = symbols.length - 1; i > 0; i -= 1) {
    const other = Math.floor(random() * (i + 1));
    [symbols[i], symbols[other]] = [symbols[other], symbols[i]];
  }
  const [minimum, maximum] = SIZE_RANGES[count];
  const [largeMinimum, largeMaximum] = LARGE_SIZE_RANGES[count];
  const pieces = symbols
    .map((symbolId, rank): Piece => ({
      symbolId,
      size:
        rank === 0
          ? largeMinimum + random() * (largeMaximum - largeMinimum)
          : Math.max(
              minimum,
              Math.min(
                maximum,
                minimum +
                  (maximum - minimum) * (1 - (rank - 1) / (count - 2)) ** 2 +
                  (random() - 0.5) * 0.008,
              ),
            ),
      rotation: random() * 360,
    }))
    .sort((a, b) => b.size - a.size);
  const result = pack(pieces, random) ?? fallback(pieces, count, random);
  if (layoutCache.size >= 256) layoutCache.delete(layoutCache.keys().next().value!);
  layoutCache.set(key, result.map((p) => ({ ...p })));
  return result;
}
